import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Wallet, TrendingDown, TrendingUp, ArrowUpRight, ArrowDownRight,
  Repeat, CreditCard, Tag, PiggyBank, Target, BarChart3, Sparkles, Users, ShoppingCart, X,
} from "lucide-react";
import { AppLayout, DashboardTopbar } from "./_shared/AppLayout";
import { brl } from "./_shared/utils";
import { getCategories, catById } from "./_shared/categories";
import { useAppData } from "./_shared/store";
import { filterByMonth, sumValor, fmtMonthLong } from "./_shared/utils";

/* ── Custom SVG donut ───────────────────────────────────────────── */
const DonutChart = ({ data, size = 260, inner = 55, outer = 105 }) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total <= 0) return null;
  const cx = size / 2, cy = size / 2;
  let acc = 0;
  const paths = data.map((d) => {
    const startA = (acc / total) * 2 * Math.PI - Math.PI / 2;
    acc += d.value;
    const endA = (acc / total) * 2 * Math.PI - Math.PI / 2;
    const large = endA - startA > Math.PI ? 1 : 0;
    const x1 = cx + outer * Math.cos(startA), y1 = cy + outer * Math.sin(startA);
    const x2 = cx + outer * Math.cos(endA),   y2 = cy + outer * Math.sin(endA);
    const x3 = cx + inner * Math.cos(endA),   y3 = cy + inner * Math.sin(endA);
    const x4 = cx + inner * Math.cos(startA), y4 = cy + inner * Math.sin(startA);
    const dPath = `M ${x1} ${y1} A ${outer} ${outer} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${inner} ${inner} 0 ${large} 0 ${x4} ${y4} Z`;
    return { id: d.id, dPath, color: d.color };
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {paths.map((p) => (
        <path key={p.id} d={p.dPath} fill={p.color} stroke="#0f172a" strokeWidth={1.5} />
      ))}
    </svg>
  );
};

/* ── Modal base ─────────────────────────────────────────────────── */
const Modal = ({ open, onClose, title, wide, children }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center px-4 pt-16 pb-4 sm:p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className={`relative z-10 w-full ${wide ? "sm:max-w-2xl" : "sm:max-w-md"} rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl max-h-[90vh] flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 shrink-0">
          <h2 className="font-display text-base font-bold text-white">{title}</h2>
          <button
            onClick={onClose}
            className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4 sm:p-5 overflow-y-auto overflow-x-hidden">{children}</div>
      </div>
    </div>
  );
};

/* ── KPI Card (top 4) ───────────────────────────────────────────── */
const KPICard = ({ label, value, sub, icon, testid, gradientClass, cardStyle, onClick }) => {
  const clickable = !!onClick;
  return (
    <div
      className={`rounded-2xl p-5 ${gradientClass} ${
        clickable
          ? "cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:brightness-110 active:scale-[0.98]"
          : ""
      }`}
      style={cardStyle}
      data-testid={testid}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      aria-label={clickable ? label : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => e.key === "Enter" && onClick() : undefined}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-white/80 font-semibold">{label}</span>
        <div className="h-8 w-8 rounded-xl bg-white/20 grid place-items-center text-white">{icon}</div>
      </div>
      <div className="mt-2 text-2xl font-display font-bold text-white">{value}</div>
      {sub && <div className="mt-1 text-xs text-white/70">{sub}</div>}
    </div>
  );
};

/* ── Gauge (Limite de Uso) ──────────────────────────────────────── */
const Gauge = ({ pct, onClick }) => {
  const color = pct >= 90 ? "#f43f5e" : pct >= 60 ? "#f59e0b" : "#10b981";
  const label = pct >= 90 ? "Crítico" : pct >= 60 ? "Atenção" : "Saudável";
  const r = 48, c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  return (
    <div
      className="rounded-2xl p-5 flex items-center gap-4 bg-gradient-to-br from-violet-600/80 to-violet-800/80 cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:brightness-110 active:scale-[0.98]"
      style={{ boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.2), 0 0 0 4px #4c1d95" }}
      data-testid="kpi-limite"
      onClick={onClick}
      role="button"
      aria-label="Limite de Uso — ver comparativo"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
    >
      <div className="relative h-20 w-20 shrink-0">
        <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
          <circle cx="60" cy="60" r={r} stroke="rgba(255,255,255,0.2)" strokeWidth="10" fill="none" />
          <circle cx="60" cy="60" r={r} stroke={color} strokeWidth="10" fill="none"
            strokeDasharray={`${dash} ${c - dash}`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 grid place-items-center">
          <div className="text-center">
            <div className="text-base font-display font-bold" style={{ color }}>{pct}%</div>
            <div className="text-[10px] text-white/70 uppercase tracking-wider">uso</div>
          </div>
        </div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-white/80 font-semibold">Limite de Uso</div>
        <div className="mt-1 font-display font-bold text-sm" style={{ color }}>{label}</div>
        <div className="text-xs text-white/70 mt-1">Gastos vs. entradas do mês</div>
      </div>
    </div>
  );
};

/* ── NavCard ────────────────────────────────────────────────────── */
const NavCard = ({ to, title, amount, subtitle, icon, accent, testid }) => (
  <Link
    to={to}
    className="group rounded-2xl p-5 bg-slate-900 border border-slate-800 hover:border-emerald-500/40 hover:bg-slate-900/80 transition block relative overflow-hidden"
    data-testid={testid}
  >
    <div className={`absolute -top-12 -right-12 h-32 w-32 rounded-full blur-2xl opacity-30 ${accent}`} />
    <div className="relative flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{title}</div>
        <div className="mt-2 text-2xl font-display font-bold text-white">{amount}</div>
        <div className="mt-1 text-xs text-slate-400">{subtitle}</div>
      </div>
      <div className="h-10 w-10 rounded-xl bg-slate-800 grid place-items-center text-emerald-300 group-hover:scale-110 transition">
        {icon}
      </div>
    </div>
  </Link>
);

/* ── Modal 2: Gastos (escolha onde ir) ──────────────────────────── */
const GastosModal = ({ open, onClose, totalFixos, totalVariaveis, totalCartoes }) => {
  const navigate = useNavigate();
  const go = (path) => { onClose(); navigate(path); };
  return (
    <Modal open={open} onClose={onClose} title="Total de Gastos — Ir para">
      <p className="text-slate-400 text-sm mb-5">Escolha onde deseja visualizar os detalhes:</p>
      <div className="space-y-3">
        <button
          onClick={() => go("/app/gastos-fixos")}
          className="w-full flex items-center justify-between rounded-xl px-4 py-4 bg-slate-800 border border-slate-700 hover:border-emerald-500/50 hover:bg-slate-800/70 transition group"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-500/15 border border-emerald-500/20 grid place-items-center">
              <Repeat className="h-5 w-5 text-emerald-400" />
            </div>
            <div className="text-left">
              <div className="text-sm font-semibold text-white">Gastos Fixos</div>
              <div className="text-xs text-slate-400 mt-0.5">Aluguel, assinaturas, contas</div>
            </div>
          </div>
          <span className="text-emerald-400 font-bold text-sm tabular-nums">{brl(totalFixos)}</span>
        </button>

        <button
          onClick={() => go("/app/gastos-variaveis")}
          className="w-full flex items-center justify-between rounded-xl px-4 py-4 bg-slate-800 border border-slate-700 hover:border-rose-500/50 hover:bg-slate-800/70 transition group"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-rose-500/15 border border-rose-500/20 grid place-items-center">
              <ArrowDownRight className="h-5 w-5 text-rose-400" />
            </div>
            <div className="text-left">
              <div className="text-sm font-semibold text-white">Gastos Variáveis</div>
              <div className="text-xs text-slate-400 mt-0.5">Mercado, lazer, refeições</div>
            </div>
          </div>
          <span className="text-rose-400 font-bold text-sm tabular-nums">{brl(totalVariaveis)}</span>
        </button>

        <button
          onClick={() => go("/app/cartoes")}
          className="w-full flex items-center justify-between rounded-xl px-4 py-4 bg-slate-800 border border-slate-700 hover:border-violet-500/50 hover:bg-slate-800/70 transition group"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-violet-500/15 border border-violet-500/20 grid place-items-center">
              <CreditCard className="h-5 w-5 text-violet-400" />
            </div>
            <div className="text-left">
              <div className="text-sm font-semibold text-white">Cartões de Crédito</div>
              <div className="text-xs text-slate-400 mt-0.5">Faturas e transações</div>
            </div>
          </div>
          <span className="text-violet-400 font-bold text-sm tabular-nums">{brl(totalCartoes)}</span>
        </button>
      </div>
    </Modal>
  );
};

/* ── Modal 3: Despesas do Mês (Saldo) ───────────────────────────── */
const DespesasModal = ({ open, onClose, GF, GV, TXmes, totalGastos, saldo, distribuicao, selectedMonth, customCategories }) => {
  const [filtro, setFiltro] = useState("todos");
  const mesLabel = fmtMonthLong(selectedMonth);

  const todos = useMemo(() => [
    ...GF.map((i) => ({ ...i, _tipo: "Fixo" })),
    ...GV.map((i) => ({ ...i, _tipo: "Variável" })),
    ...TXmes.map((i) => ({ ...i, _tipo: "Cartão" })),
  ].sort((a, b) => Number(b.valor || 0) - Number(a.valor || 0)), [GF, GV, TXmes]);

  const lista = filtro === "todos" ? todos
    : filtro === "fixos" ? GF.map((i) => ({ ...i, _tipo: "Fixo" }))
    : filtro === "variaveis" ? GV.map((i) => ({ ...i, _tipo: "Variável" }))
    : TXmes.map((i) => ({ ...i, _tipo: "Cartão" }));

  const tipoCor = { Fixo: "text-emerald-400 bg-emerald-500/10", Variável: "text-rose-400 bg-rose-500/10", Cartão: "text-violet-400 bg-violet-500/10" };

  return (
    <Modal open={open} onClose={onClose} title={`Despesas de ${mesLabel}`} wide>
      {/* Dois cards KPI lado a lado */}
      {(() => { const saldoPos = saldo >= 0; return (
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="despesas-kpi-card rounded-xl py-3 px-4 text-center overflow-hidden" data-variant="red" style={{ background: "radial-gradient(ellipse at center, #ef4444 0%, #991b1b 100%)" }}>
          <div className="text-xs text-white/70 uppercase tracking-wider mb-1">Total de Despesas</div>
          <div className="text-lg sm:text-2xl font-display font-bold text-white tabular-nums">{brl(totalGastos)}</div>
          <div className="text-xs text-white/60 mt-1">{todos.length} lançamentos no mês</div>
        </div>
        <div className="despesas-kpi-card rounded-xl py-3 px-4 text-center overflow-hidden" data-variant={saldoPos ? "green" : "red"}
          style={{ background: saldoPos
            ? "radial-gradient(ellipse at center, #22c55e 0%, #14532d 100%)"
            : "radial-gradient(ellipse at center, #ef4444 0%, #991b1b 100%)" }}>
          <div className="text-xs text-white/70 uppercase tracking-wider mb-1">Saldo do Mês</div>
          <div className="text-lg sm:text-2xl font-display font-bold text-white tabular-nums">{brl(saldo)}</div>
          <div className="text-xs text-white/60 mt-1">Entradas – Gastos</div>
        </div>
      </div>
      ); })()}

      {/* Resumo por tipo */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          { key: "fixos",     label: "Fixos",     valor: GF,    cor: "emerald" },
          { key: "variaveis", label: "Variáveis",  valor: GV,    cor: "rose" },
          { key: "cartoes",   label: "Cartões",    valor: TXmes, cor: "violet" },
        ].map(({ key, label, valor, cor }) => (
          <button key={key}
            onClick={() => setFiltro((f) => f === key ? "todos" : key)}
            className={`rounded-xl p-3 text-center border transition-all overflow-hidden ${
              filtro === key
                ? `border-${cor}-500/60 bg-${cor}-500/15`
                : "border-slate-700 bg-slate-800/50 hover:border-slate-600"
            }`}
          >
            <div className={`text-xs sm:text-sm font-bold text-${cor}-400 truncate`}>{brl(sumValor(valor))}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">{label}</div>
          </button>
        ))}
      </div>

      {/* Mini donut + top categorias */}
      {distribuicao.length > 0 && (
        <div className="rounded-xl bg-slate-800/50 border border-slate-700 p-4 mb-4">
          <div className="flex items-center gap-4">
            <DonutChart data={distribuicao.slice(0, 8)} size={100} inner={28} outer={46} />
            <div className="flex-1 space-y-1.5 min-w-0">
              {distribuicao.slice(0, 5).map((d) => {
                const pct = ((d.value / totalGastos) * 100).toFixed(1);
                return (
                  <div key={d.id} className="flex items-center justify-between text-xs gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ background: d.color }} />
                      <span className="text-slate-300 truncate">{d.name}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-slate-500">{pct}%</span>
                      <span className="text-white font-semibold tabular-nums">{brl(d.value)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Lista de lançamentos */}
      <div className="despesas-lista space-y-1.5">
        <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">
          {filtro === "todos" ? "Todos os lançamentos" : lista[0]?._tipo + "s"} ({lista.length})
        </div>
        {lista.length === 0 && (
          <div className="text-center text-slate-500 text-sm py-6">Nenhum lançamento neste período</div>
        )}
        {lista.map((item, i) => {
          const cat = catById(item.cat, customCategories);
          return (
            <div key={item.id || i} className="flex items-center justify-between rounded-xl px-3 py-2.5 bg-slate-800/40 hover:bg-slate-800 transition">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="h-2 w-2 rounded-full shrink-0" style={{ background: cat.color }} />
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{item.descricao || item.nome || "—"}</div>
                  <div className="text-[10px] text-slate-500">{cat.name}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${tipoCor[item._tipo]}`}>
                  {item._tipo}
                </span>
                <span className="text-sm font-bold text-rose-300 tabular-nums">{brl(item.valor)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

/* ── Modal 4: Comparativo Entradas vs Gastos ────────────────────── */
const ComparativoModal = ({ open, onClose, totalEntradas, totalGastos, totalFixos, totalVariaveis, totalCartoes, saldo, limitePct, distribuicao, selectedMonth }) => {
  const mesLabel = fmtMonthLong(selectedMonth);
  const maxBar = Math.max(totalEntradas, totalGastos, 1);
  const saldoPositivo = saldo >= 0;
  const corSaldo = saldoPositivo ? "#34d399" : "#f43f5e";

  return (
    <Modal open={open} onClose={onClose} title={`Entradas vs Gastos — ${mesLabel}`} wide>
      {/* KPIs lado a lado */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="rounded-xl p-3 text-center" style={{ background: "linear-gradient(135deg,#052e16,#0f2615)", border: "1px solid rgba(52,211,153,.2)" }}>
          <div className="text-[10px] text-emerald-300/70 uppercase tracking-wider mb-1">Entradas</div>
          <div className="text-base sm:text-2xl font-display font-bold text-emerald-300 tabular-nums truncate">{brl(totalEntradas)}</div>
        </div>
        <div className="rounded-xl p-3 text-center bg-gradient-to-br from-violet-600/80 to-violet-800/80" style={{ border: "1px solid rgba(167,139,250,.3)" }}>
          <div className="text-[10px] text-violet-200/80 uppercase tracking-wider mb-1">Gastos</div>
          <div className="text-base sm:text-2xl font-display font-bold text-white tabular-nums truncate">{brl(totalGastos)}</div>
        </div>
      </div>

      {/* Barras comparativas */}
      <div className="rounded-xl bg-slate-800/50 border border-slate-700 p-4 mb-4 space-y-3">
        <div>
          <div className="flex justify-between text-xs text-slate-400 mb-1.5">
            <span className="text-emerald-400 font-medium">Entradas</span>
            <span>{brl(totalEntradas)}</span>
          </div>
          <div className="h-3 rounded-full bg-slate-700 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all"
              style={{ width: `${(totalEntradas / maxBar) * 100}%` }} />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-xs text-slate-400 mb-1.5">
            <span className="text-rose-400 font-medium">Gastos</span>
            <span>{brl(totalGastos)}</span>
          </div>
          <div className="h-3 rounded-full bg-slate-700 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-rose-500 to-rose-400 transition-all"
              style={{ width: `${(totalGastos / maxBar) * 100}%` }} />
          </div>
        </div>
      </div>

      {/* Saldo + % uso */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="rounded-xl p-3 text-center bg-slate-800/50 border border-slate-700">
          <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Saldo Final</div>
          <div className="text-base sm:text-xl font-display font-bold tabular-nums truncate" style={{ color: corSaldo }}>
            {brl(saldo)}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">{saldoPositivo ? "Sobrou no mês" : "Deficit no mês"}</div>
        </div>
        <div className="rounded-xl p-3 text-center bg-slate-800/50 border border-slate-700">
          <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">% do Orçamento</div>
          <div className="text-xl font-display font-bold tabular-nums"
            style={{ color: limitePct >= 90 ? "#f43f5e" : limitePct >= 60 ? "#f59e0b" : "#34d399" }}>
            {limitePct}%
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            {limitePct >= 90 ? "Crítico" : limitePct >= 60 ? "Atenção" : "Saudável"}
          </div>
        </div>
      </div>

      {/* Breakdown de gastos */}
      <div className="rounded-xl bg-slate-800/50 border border-slate-700 p-4 mb-4">
        <div className="text-xs text-slate-400 uppercase tracking-wider mb-3">Composição dos Gastos</div>
        <div className="space-y-2">
          {[
            { label: "Gastos Fixos",     valor: totalFixos,     cor: "#34d399", icon: <Repeat className="h-3.5 w-3.5" /> },
            { label: "Gastos Variáveis", valor: totalVariaveis, cor: "#f43f5e", icon: <ArrowDownRight className="h-3.5 w-3.5" /> },
            { label: "Cartões",          valor: totalCartoes,   cor: "#a78bfa", icon: <CreditCard className="h-3.5 w-3.5" /> },
          ].map(({ label, valor, cor, icon }) => (
            <div key={label}>
              <div className="flex justify-between text-xs mb-1" style={{ color: cor }}>
                <span className="flex items-center gap-1">{icon} {label}</span>
                <span className="font-semibold tabular-nums">{brl(valor)}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-700 overflow-hidden">
                <div className="h-full rounded-full transition-all"
                  style={{ width: `${totalGastos > 0 ? (valor / totalGastos) * 100 : 0}%`, background: cor }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top categorias */}
      {distribuicao.length > 0 && (
        <div>
          <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Maiores Categorias</div>
          <div className="space-y-1.5">
            {distribuicao.slice(0, 5).map((d) => {
              const pct = totalGastos > 0 ? ((d.value / totalGastos) * 100).toFixed(1) : "0.0";
              return (
                <div key={d.id} className="flex items-center gap-3">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: d.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className="text-slate-300 truncate">{d.name}</span>
                      <span className="text-white font-semibold tabular-nums ml-2">{brl(d.value)}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-700 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: d.color }} />
                    </div>
                  </div>
                  <span className="text-[10px] text-slate-500 shrink-0 w-8 text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Modal>
  );
};

/* ── Dashboard principal ────────────────────────────────────────── */
const Dashboard = () => {
  const navigate = useNavigate();
  const { data } = useAppData();
  const { gastosFixos, gastosVariaveis, cartoes, investimentos, objetivos, entradas, selectedMonth } = data;

  const [modal, setModal] = useState(null); // "gastos" | "despesas" | "comparativo"
  const closeModal = () => setModal(null);

  const GF    = useMemo(() => filterByMonth(gastosFixos, selectedMonth),    [gastosFixos, selectedMonth]);
  const GV    = useMemo(() => filterByMonth(gastosVariaveis, selectedMonth), [gastosVariaveis, selectedMonth]);
  const ENT   = useMemo(() => filterByMonth(entradas, selectedMonth),        [entradas, selectedMonth]);
  const TXmes = useMemo(() => cartoes.flatMap((c) => filterByMonth(c.transacoes, selectedMonth)), [cartoes, selectedMonth]);

  const distribuicao = useMemo(() => {
    const map = {};
    [...GF, ...GV, ...TXmes].forEach((g) => {
      map[g.cat] = (map[g.cat] || 0) + Number(g.valor || 0);
    });
    return Object.entries(map)
      .map(([id, value]) => ({ id, name: catById(id, data.customCategories).name, value, color: catById(id, data.customCategories).color }))
      .sort((a, b) => b.value - a.value);
  }, [GF, GV, TXmes]);

  const totalFixos     = sumValor(GF);
  const totalVariaveis = sumValor(GV);
  const totalCartoes   = sumValor(TXmes);
  const totalEntradas  = sumValor(ENT);
  const totalGastos    = totalFixos + totalVariaveis + totalCartoes;
  const saldo          = totalEntradas - totalGastos;
  const limitePct      = totalEntradas > 0 ? Math.min(100, Math.round((totalGastos / totalEntradas) * 100)) : 0;

  return (
    <AppLayout>
      <DashboardTopbar />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">

        {/* Month header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <span className="pill"><Sparkles className="h-3.5 w-3.5" /> DASHBOARD MONYXX</span>
            <h1 className="mt-3 font-display text-3xl md:text-4xl font-bold capitalize">
              Mês de <span className="monyx-grad-text">{fmtMonthLong(selectedMonth)}</span>
            </h1>
          </div>
          <Link to="/app/dashboard-anual" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 border border-slate-700 text-sm hover:border-emerald-500/40 hover:text-emerald-300 transition" data-testid="nav-dashboard-anual">
            <BarChart3 className="h-4 w-4" /> Visão Anual
          </Link>
        </div>

        {/* Top KPIs — clicáveis */}
        <div className="mt-6 grid md:grid-cols-4 gap-4">
          <KPICard
            testid="kpi-entradas"
            label="Total de Entradas"
            value={brl(totalEntradas)}
            sub="Receitas confirmadas"
            icon={<TrendingUp className="h-4 w-4" />}
            gradientClass="bg-gradient-to-br from-blue-600/80 to-blue-800/80"
            cardStyle={{ boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.2), 0 0 0 4px #1e40af" }}
            onClick={() => navigate("/app/entradas")}
          />
          <KPICard
            testid="kpi-gastos"
            label="Total de Gastos"
            value={brl(totalGastos)}
            sub="Fixos + Variáveis + Cartão"
            icon={<TrendingDown className="h-4 w-4" />}
            gradientClass="bg-gradient-to-br from-emerald-600/80 to-emerald-800/80"
            cardStyle={{ boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.2), 0 0 0 4px #065f46" }}
            onClick={() => setModal("gastos")}
          />
          <KPICard
            testid="kpi-saldo"
            label="Saldo do Mês"
            value={brl(saldo)}
            sub="Entradas − Gastos"
            icon={<Wallet className="h-4 w-4" />}
            gradientClass="bg-gradient-to-br from-rose-600/80 to-rose-800/80"
            cardStyle={{ boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.2), 0 0 0 4px #9f1239" }}
            onClick={() => setModal("despesas")}
          />
          <Gauge pct={limitePct} onClick={() => setModal("comparativo")} />
        </div>

        {/* Nav cards + Pie */}
        <div className="mt-6 grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
            <NavCard testid="card-fixos"         to="/app/gastos-fixos"     title="Gastos Fixos"       amount={brl(totalFixos)}    subtitle={`${GF.length} lançamentos`}                   icon={<Repeat className="h-5 w-5" />}          accent="bg-emerald-500" />
            <NavCard testid="card-variaveis"     to="/app/gastos-variaveis" title="Gastos Variáveis"   amount={brl(totalVariaveis)} subtitle={`${GV.length} lançamentos`}               icon={<ArrowDownRight className="h-5 w-5" />}  accent="bg-rose-500" />
            <NavCard testid="card-entradas"      to="/app/entradas"         title="Entradas"           amount={brl(totalEntradas)}  subtitle="Salário, freelas, dividendos"                 icon={<ArrowUpRight className="h-5 w-5" />}    accent="bg-blue-500" />
            <NavCard testid="card-cartoes"       to="/app/cartoes"          title="Cartões de Crédito" amount={brl(totalCartoes)}   subtitle={`${cartoes.length} cartões ativos`}           icon={<CreditCard className="h-5 w-5" />}      accent="bg-violet-500" />
            <NavCard testid="card-categorias"    to="/app/categorias"       title="Categorias"         amount={`${getCategories(data.customCategories).length} grupos`} subtitle="Significados e cores" icon={<Tag className="h-5 w-5" />}     accent="bg-amber-500" />
            <NavCard testid="card-investimentos" to="/app/investimentos"    title="Investimentos"      amount={brl(investimentos.resumo.patrimonio)} subtitle="Ações, FIIs e renda fixa"   icon={<PiggyBank className="h-5 w-5" />}       accent="bg-cyan-500" />
            <NavCard testid="card-objetivos"     to="/app/objetivos"        title="Meus Objetivos"     amount={`${objetivos.length} metas`} subtitle="Progresso e planejamento"           icon={<Target className="h-5 w-5" />}          accent="bg-pink-500" />
            <NavCard testid="card-anual"         to="/app/dashboard-anual"  title="Dashboard Anual"    amount="2026"              subtitle="Fluxo e resumo do ano"                          icon={<BarChart3 className="h-5 w-5" />}       accent="bg-teal-500" />
            <NavCard testid="card-familia"       to="/app/familia"          title="Família"            amount="Juntos"            subtitle="Finanças a dois, um painel"                     icon={<Users className="h-5 w-5" />}           accent="bg-violet-500" />
            <NavCard testid="card-compras"       to="/app/lista-de-compras" title="Lista de Compras"   amount="Mercado"           subtitle="Itens e quantidades"                            icon={<ShoppingCart className="h-5 w-5" />}    accent="bg-orange-500" />
          </div>

          {/* Distribuição */}
          <div className="rounded-2xl p-5 bg-slate-900 border border-slate-800" data-testid="pie-distribuicao">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display text-lg font-bold">Distribuição de Gastos</h3>
                <p className="text-xs text-slate-400">Por categoria no mês</p>
              </div>
            </div>
            <div className="mt-4" style={{ width: 260, margin: "0 auto" }}>
              <DonutChart data={distribuicao} size={260} inner={55} outer={105} />
            </div>
            <div className="mt-3 space-y-1.5 max-h-44 overflow-y-auto pr-1">
              {distribuicao.map((d) => {
                const total = distribuicao.reduce((s, x) => s + x.value, 0);
                const pct = ((d.value / total) * 100).toFixed(1);
                return (
                  <div key={d.id} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: d.color }} />
                      <span className="text-slate-200">{d.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-slate-400">{pct}%</span>
                      <span className="text-white font-semibold tabular-nums">{brl(d.value)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Família promo banner */}
        <div className="mt-6 rounded-2xl overflow-hidden border border-violet-500/30"
          style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #2d1b69 50%, #1e293b 100%)" }}>
          <div className="px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-2xl bg-violet-500/20 border border-violet-500/30 grid place-items-center shrink-0">
                <Users className="h-6 w-6 text-violet-300" />
              </div>
              <div>
                <div className="text-sm font-bold text-white">Módulo Família — Finanças a dois</div>
                <div className="text-xs text-violet-300/80 mt-0.5">Gerencie as finanças do casal juntos: painel combinado, divisão de despesas, metas e saldos mensais.</div>
              </div>
            </div>
            <Link to="/app/familia"
              className="shrink-0 px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold transition whitespace-nowrap">
              Abrir Família →
            </Link>
          </div>
        </div>

      </div>

      {/* Modais dos KPI cards */}
      <GastosModal
        open={modal === "gastos"}
        onClose={closeModal}
        totalFixos={totalFixos}
        totalVariaveis={totalVariaveis}
        totalCartoes={totalCartoes}
      />
      <DespesasModal
        open={modal === "despesas"}
        onClose={closeModal}
        GF={GF} GV={GV} TXmes={TXmes}
        totalGastos={totalGastos}
        saldo={saldo}
        distribuicao={distribuicao}
        selectedMonth={selectedMonth}
        customCategories={data.customCategories}
      />
      <ComparativoModal
        open={modal === "comparativo"}
        onClose={closeModal}
        totalEntradas={totalEntradas}
        totalGastos={totalGastos}
        totalFixos={totalFixos}
        totalVariaveis={totalVariaveis}
        totalCartoes={totalCartoes}
        saldo={saldo}
        limitePct={limitePct}
        distribuicao={distribuicao}
        selectedMonth={selectedMonth}
      />
    </AppLayout>
  );
};

export default Dashboard;
